import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Add, Visibility } from '@mui/icons-material';
import { repAPI } from '../../services/api';

const LEAD_STATUSES = ['NEW', 'CONTACTED', 'QUALIFIED', 'PROPOSAL_SENT', 'NEGOTIATING', 'CLOSED_WON', 'CLOSED_LOST', 'PENDING_ADMIN_APPROVAL'];
const LEAD_SOURCES = ['COLD_CALL', 'REFERRAL', 'WEBSITE', 'TRADE_SHOW', 'EMAIL_CAMPAIGN', 'SOCIAL_MEDIA', 'OTHER'];

const CRMLeads = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    clinicName: '',
    contactName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    source: 'OTHER',
    status: 'NEW',
    priority: 3,
  });

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const response = await repAPI.getLeads();
      if (response.success) {
        setLeads(response.data);
      }
    } catch (err) {
      setError('Failed to load leads');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.clinicName || !formData.clinicName.trim()) {
      setError('Clinic name is required');
      return;
    }
    if (!formData.contactName || !formData.contactName.trim()) {
      setError('Contact name is required');
      return;
    }
    if (!formData.email || !formData.email.trim()) {
      setError('Email is required');
      return;
    }
    
    try {
      setError('');
      const response = await repAPI.createLead(formData);
      
      // Handle both response.success and direct success formats
      if (response?.success !== false) {
        setSuccess('Lead created successfully');
        fetchLeads();
        setOpenDialog(false);
        setFormData({
          clinicName: '',
          contactName: '',
          email: '',
          phone: '',
          address: '',
          city: '',
          state: '',
          zipCode: '',
          source: 'OTHER',
          status: 'NEW',
          priority: 3,
        });
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.error || response.message || 'Failed to create lead');
      }
    } catch (err) {
      console.error('Error creating lead:', err);
      setError(err.message || 'Failed to create lead. Please check all required fields and try again.');
    }
  };

  const handlePushToAdmin = async (leadId) => {
    try {
      await repAPI.pushLeadToAdmin(leadId);
      setSuccess('Lead pushed to admin for approval');
      fetchLeads();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to push lead');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      NEW: 'default',
      CONTACTED: 'info',
      QUALIFIED: 'primary',
      PROPOSAL_SENT: 'secondary',
      PENDING_ADMIN_APPROVAL: 'warning',
      CLOSED_WON: 'success',
      CLOSED_LOST: 'error',
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">CRM / Leads</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setOpenDialog(true)}>
          Create Lead
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Clinic Name</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary">No leads yet. Create your first lead to get started.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              leads.map((lead) => (
                <TableRow 
                  key={lead.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    // Don't navigate if clicking on buttons/actions
                    if (e.target.closest('button') || e.target.closest('a')) {
                      return;
                    }
                    navigate(`/rep/crm-leads/${lead.id}`);
                  }}
                >
                  <TableCell>{lead.clinicName}</TableCell>
                  <TableCell>{lead.contactName}</TableCell>
                  <TableCell>{lead.email}</TableCell>
                  <TableCell>
                    <Chip label={lead.status} color={getStatusColor(lead.status)} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip label={lead.priority === 1 ? 'High' : lead.priority === 2 ? 'Med' : 'Low'} size="small" />
                  </TableCell>
                  <TableCell>{lead.source}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/rep/crm-leads/${lead.id}`)}
                        title="View Details"
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                      {lead.status !== 'PENDING_ADMIN_APPROVAL' && lead.status !== 'CONVERTED_TO_CLIENT' && (
                        <Button 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePushToAdmin(lead.id);
                          }}
                        >
                          Push to Admin
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Lead</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
            <TextField label="Clinic Name" value={formData.clinicName} onChange={(e) => setFormData({ ...formData, clinicName: e.target.value })} />
            <TextField label="Contact Name" value={formData.contactName} onChange={(e) => setFormData({ ...formData, contactName: e.target.value })} />
            <TextField label="Email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
            <TextField label="Phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
            <TextField label="Address" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} fullWidth sx={{ gridColumn: '1 / -1' }} />
            <TextField label="City" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
            <TextField label="State" value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} />
            <TextField label="Zip Code" value={formData.zipCode} onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })} />
            <TextField select label="Source" value={formData.source} onChange={(e) => setFormData({ ...formData, source: e.target.value })}>
              {LEAD_SOURCES.map((source) => <MenuItem key={source} value={source}>{source}</MenuItem>)}
            </TextField>
            <TextField select label="Priority" value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}>
              <MenuItem value={1}>High</MenuItem>
              <MenuItem value={2}>Medium</MenuItem>
              <MenuItem value={3}>Low</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!formData.clinicName || !formData.email}>
            Create Lead
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CRMLeads;

